// Emoji for the chat: the picker's groups, search by Russian names and keywords and by shortcodes
// (`:+1:`), and the emoji the user took last. The data (tools/emoji.mjs, `/emoji.json`) is loaded the first
// time something needs it; the emoji the user took are kept in this browser.
import { keep, stored } from "./account.ts";
import { changed } from "./store.ts";

/** An emoji: itself, its name, its keywords and its shortcodes (space-separated). */
export type Emoji = { e: string; name: string; words: string; codes: string };
export type Group = { key: string; name: string; emoji: Emoji[] };

/** The groups, once loaded; whether they are being loaded. */
let groups: Group[] | null = null;
let loading: Promise<Group[]> | null = null;
/** Emoji by shortcode. */
const byCode = new Map<string, string>();

/** How many emoji the user took the picker keeps, the last first. */
const RECENT = 24;
/** What the picker shows before the user took any. */
const FIRST = ["👍", "❤️", "😂", "🎉", "🙏", "👀", "🔥", "✅", "😊", "🤔", "👏", "😢"];

/** The emoji data, loaded if it is not yet (the page is drawn again when it is). */
export function emojiGroups(): Group[] | null {
  if (groups === null) void loadEmoji();
  return groups;
}

export function loadEmoji(): Promise<Group[]> {
  if (loading !== null) return loading;
  loading = (async () => {
    const r = await fetch("/emoji.json");
    const d = (await r.json()) as { groups: { key: string; name: string; emoji: [string, string, string, string][] }[] };
    groups = d.groups.map((g) => ({
      key: g.key,
      name: g.name,
      emoji: g.emoji.map(([e, name, words, codes]) => ({ e, name, words: words.toLowerCase(), codes })),
    }));
    for (const g of groups) for (const x of g.emoji) for (const c of x.codes.split(" ")) if (c !== "") byCode.set(c, x.e);
    changed();
    return groups;
  })().catch((err) => {
    loading = null;
    throw err;
  });
  return loading;
}

/** The emoji whose name, keywords or shortcodes have `query`, the best first — the name or a shortcode
 * that is it, then one that starts with it, then a keyword that does, then any that has it; at most
 * `most`. */
export function searchEmoji(query: string, most = 64): Emoji[] {
  const q = query.trim().toLowerCase().replace(/^:|:$/g, "");
  if (q === "" || groups === null) return [];
  const ranks: Emoji[][] = [[], [], [], []];
  for (const g of groups) {
    for (const x of g.emoji) {
      const name = x.name.toLowerCase();
      const codes = x.codes.split(" ");
      if (name === q || codes.includes(q)) ranks[0].push(x);
      else if (name.startsWith(q) || codes.some((c) => c.startsWith(q))) ranks[1].push(x);
      else if (x.words.split(" ").some((w) => w.startsWith(q))) ranks[2].push(x);
      else if (name.includes(q) || x.words.includes(q) || x.codes.includes(q)) ranks[3].push(x);
    }
  }
  return ranks.flat().slice(0, most);
}

/** Emoji `e` in one form (as tools/emoji.mjs makes the data): without the selector of emoji
 * presentation after a character that shows as an emoji without it, so that the same emoji is the same
 * text — a reaction, say. */
export function canonical(e: string): string {
  return e.replace(/(\p{Emoji_Presentation})\uFE0F/gu, "$1");
}

/** The emoji of shortcode `code` (without colons), if the data is loaded and has it. */
export function emojiOf(code: string): string | null {
  return byCode.get(code.toLowerCase()) ?? null;
}

/** The emoji the user took last, the last first — some to begin with. */
export function recentEmoji(): string[] {
  try {
    const kept = JSON.parse(stored("xq.emoji") ?? "[]") as unknown;
    if (Array.isArray(kept) && kept.length > 0) return kept.filter((x): x is string => typeof x === "string");
  } catch {
    // Not kept, or kept wrong: the first ones.
  }
  return FIRST;
}

/** The user took `emoji`: it goes first among the last ones. */
export function tookEmoji(emoji: string): void {
  const list = [emoji, ...recentEmoji().filter((x) => x !== emoji)].slice(0, RECENT);
  keep("xq.emoji", JSON.stringify(list));
}
